/** Side-nav config builder, driven by typed registry entries and server `visibleNavTags`. */
import type { SideNavItem } from "@performyard/py-components";

import type { NavigationConfig } from "../../api/services/defs/navigation";

import {
	ENGAGEMENT_SUBNAV_ORDER,
	FEEDBACK_VIEW_ORDER,
	SIDE_NAV_GROUPS,
	SIDE_NAV_REGISTRY,
	SIDE_NAV_SOURCE_ACTIVE_RULES,
	type SideNavContext,
	hasVisibleTag,
	resolveSideNavRegistryItem,
} from "./side-nav-registry";

type LeafItem = Extract<SideNavItem, { type: "item" }>;
type GroupItem = Extract<SideNavItem, { type: "group" }>;

const leaf = (key: keyof typeof SIDE_NAV_REGISTRY, context: SideNavContext): LeafItem =>
	resolveSideNavRegistryItem(SIDE_NAV_REGISTRY[key], context);

const group = (key: keyof typeof SIDE_NAV_GROUPS, children: LeafItem[]): SideNavItem | null => {
	if (children.length === 0) return null;
	return { ...SIDE_NAV_GROUPS[key], type: "group", children };
};

const goalsGroup = (config: NavigationConfig, context: SideNavContext): SideNavItem | null => {
	const children: LeafItem[] = [];

	if (hasVisibleTag(config, "performance", "goals")) {
		children.push(leaf("myGoals", context));
	}

	if (hasVisibleTag(config, "goals")) {
		if (config.hasDirectReports) {
			children.push(leaf("myTeamGoals", context));
		}
		if (hasVisibleTag(config, "goals", "list")) {
			children.push(leaf("companyGoalsList", context));
		}
		if (hasVisibleTag(config, "goals", "dashboard")) {
			children.push(leaf("companyGoalsOverview", context));
		}
		if (hasVisibleTag(config, "goals", "alignment")) {
			children.push(leaf("companyGoalsAlignment", context));
		}
		if (hasVisibleTag(config, "goals", "employees-without-goals")) {
			children.push(leaf("employeesWithoutGoals", context));
		}
	}

	return group("goals", children);
};

const reviewsGroup = (config: NavigationConfig, context: SideNavContext): SideNavItem | null => {
	const canViewPersonalReviews = hasVisibleTag(config, "performance", "reviews");
	const canViewCompanyReviews = hasVisibleTag(config, "company", "reviews");
	const children: LeafItem[] = [];

	if (canViewPersonalReviews) {
		for (const key of ["formsINeedToDo", "formsIDid", "reviewsOfMe", "reviewsOfMyTeam"] as const) {
			const tag = SIDE_NAV_REGISTRY[key].visibility?.tag;
			if (tag && hasVisibleTag(config, "reviews", tag)) {
				children.push(leaf(key, context));
			}
		}
	}

	if (canViewCompanyReviews) {
		for (const key of ["reviewCycles", "allReviews", "allForms", "assignments", "performanceGraphs"] as const) {
			const tag = SIDE_NAV_REGISTRY[key].visibility?.tag;
			if (tag && hasVisibleTag(config, "reviews", tag)) {
				children.push(leaf(key, context));
			}
		}
	}

	return group("reviews", children);
};

const FEEDBACK_REGISTRY_KEYS = {
	received: "feedbackReceived",
	given: "feedbackGiven",
	team: "feedbackTeam",
	notes: "feedbackNotes",
	requests: "feedbackRequests",
	outgoing: "feedbackOutgoing",
} as const;

const feedbackGroup = (config: NavigationConfig, context: SideNavContext): SideNavItem | null => {
	const isAdmin = config.isAdmin || config.isBasicAdmin;
	const children: LeafItem[] = [];

	if (hasVisibleTag(config, "performance", "feedback")) {
		for (const view of FEEDBACK_VIEW_ORDER) {
			if (!hasVisibleTag(config, "feedback", view)) continue;
			children.push(leaf(FEEDBACK_REGISTRY_KEYS[view], context));
		}
	}

	if (isAdmin && hasVisibleTag(config, "company", "feedback")) {
		children.push(leaf("allFeedback", context));
	}

	return group("feedback", children);
};

const meetingsGroup = (config: NavigationConfig, context: SideNavContext): SideNavItem | null => {
	if (!hasVisibleTag(config, "meeting-workspaces")) return null;
	const children: LeafItem[] = [leaf("workspaces", context), leaf("actionItems", context)];

	if ((config.isAdmin || config.isBasicAdmin) && hasVisibleTag(config, "company", "meetings")) {
		children.push(leaf("allMeetings", context));
	}

	return group("meetings", children);
};

const workflowsGroup = (config: NavigationConfig, context: SideNavContext): SideNavItem | null => {
	if (!hasVisibleTag(config, "company", "workflows")) return null;
	const children: LeafItem[] = [];

	if (config.isAdmin || config.isBasicAdmin) {
		children.push(leaf("allWorkflows", context));
	}
	children.push(leaf("allTodos", context));

	return group("workflows", children);
};

const documentsGroup = (config: NavigationConfig, context: SideNavContext): SideNavItem | null => {
	const children: LeafItem[] = [];

	if (hasVisibleTag(config, "performance", "documents")) {
		children.push(leaf("myDocuments", context));
	}
	if (hasVisibleTag(config, "company", "resources")) {
		children.push(leaf("companyDocuments", context));
		children.push(leaf("companyLinks", context));
	}

	return group("documents", children);
};

const ENGAGEMENT_REGISTRY_KEYS = {
	overview: "engagementOverview",
	cohorts: "engagementCohorts",
	factors: "engagementFactors",
	questions: "engagementQuestions",
	enps: "engagementEnps",
} as const;

const engagementGroup = (config: NavigationConfig, context: SideNavContext): SideNavItem | null => {
	const children = ENGAGEMENT_SUBNAV_ORDER.filter((key) => hasVisibleTag(config, "engagement", key)).map((key) =>
		leaf(ENGAGEMENT_REGISTRY_KEYS[key], context)
	);
	if (children.length === 0) return null;
	if (children.length === 1) {
		const only = children[0];
		return {
			id: "engagement",
			type: "item",
			label: "Engagement",
			href: only.href,
			icon: SIDE_NAV_GROUPS.engagement.icon,
		};
	}
	return group("engagement", children);
};

export const calculateSideNavConfig = (config: NavigationConfig, theUserId: string): SideNavItem[] => {
	const isAdmin = config.isAdmin || config.isBasicAdmin;
	const context = { theUserId };
	const items: SideNavItem[] = [];

	if (config.isEngagementOnly) {
		items.push(leaf("dashboard", context));
		if (hasVisibleTag(config, "company", "directory")) {
			items.push(leaf("directory", context));
		}
		const engagement = engagementGroup(config, context);
		if (engagement) items.push(engagement);
		if (hasVisibleTag(config, "surveys")) {
			items.push(leaf("surveys", context));
		}
		if (isAdmin && hasVisibleTag(config, "administration")) {
			items.push({ id: "administration-cat", type: "category", label: "Administration" });
			items.push(leaf("companySettings", context));
		}
		return items;
	}

	items.push(leaf("dashboard", context));

	for (const builder of [goalsGroup, reviewsGroup, feedbackGroup, meetingsGroup, workflowsGroup, documentsGroup]) {
		const item = builder(config, context);
		if (item) items.push(item);
	}

	if (hasVisibleTag(config, "company", "directory")) {
		items.push(leaf("directory", context));
	}
	if (hasVisibleTag(config, "company", "org-chart")) {
		items.push(leaf("orgChart", context));
	}
	const engagement = engagementGroup(config, context);
	if (engagement) items.push(engagement);
	if (hasVisibleTag(config, "surveys")) {
		items.push(leaf("surveys", context));
	}
	if (hasVisibleTag(config, "compensation")) {
		items.push(leaf("compensation", context));
	}
	if (hasVisibleTag(config, "analytics") || hasVisibleTag(config, "reporting")) {
		items.push(leaf("analytics", context));
	}

	if (isAdmin && hasVisibleTag(config, "administration")) {
		items.push({ id: "administration-cat", type: "category", label: "Administration" });
		items.push(leaf("companySettings", context));
	}

	return items;
};

const parseSearch = (search: string): URLSearchParams => {
	const stripped = search.startsWith("?") ? search.slice(1) : search;
	return new URLSearchParams(stripped);
};

const stripTrailingSlash = (s: string): string => s.replace(/\/$/, "");

const splitHref = (href: string): { path: string; search: string } => {
	const queryIdx = href.indexOf("?");
	if (queryIdx === -1) return { path: href, search: "" };
	return { path: href.slice(0, queryIdx), search: href.slice(queryIdx + 1) };
};

const PATH_LENGTH_WEIGHT = 1_000_000;
const QUERY_MATCH_BONUS = 1_000;
const QUERY_MISS_PENALTY = 1;

const hasItemWithId = (nodes: SideNavItem[], id: string): boolean =>
	nodes.some((node) =>
		node.type === "group" ? hasItemWithId(node.children, id) : node.type === "item" && node.id === id
	);

export const findActiveSideNavKey = (items: SideNavItem[], pathname: string, search = ""): string | null => {
	const targetPath = stripTrailingSlash(pathname);
	const targetParams = parseSearch(search);

	// Honor where the user came from: a detail route can highlight a different nav item when the
	// URL carries an originating `source` (e.g. a workspace opened from Company Meetings).
	const source = targetParams.get("source");
	if (source) {
		const rule = SIDE_NAV_SOURCE_ACTIVE_RULES.find(
			(r) => r.source === source && targetPath.startsWith(`${stripTrailingSlash(r.pathPrefix)}/`)
		);
		if (rule && hasItemWithId(items, rule.itemId)) return rule.itemId;
	}

	let bestId: string | null = null;
	let bestScore = -1;

	const walk = (nodes: SideNavItem[]) => {
		for (const node of nodes) {
			if (node.type === "item") {
				const { path, search: itemSearch } = splitHref(node.href);
				const normalizedPath = stripTrailingSlash(path);
				const itemParams = parseSearch(itemSearch);

				const pathMatches =
					normalizedPath === ""
						? targetPath === ""
						: targetPath === normalizedPath || targetPath.startsWith(`${normalizedPath}/`);
				if (!pathMatches) continue;

				let matched = 0;
				let missed = 0;
				for (const [key, expected] of itemParams.entries()) {
					if (targetParams.get(key) === expected) matched += 1;
					else missed += 1;
				}

				const score =
					normalizedPath.length * PATH_LENGTH_WEIGHT +
					matched * QUERY_MATCH_BONUS -
					missed * QUERY_MISS_PENALTY;
				if (score > bestScore) {
					bestScore = score;
					bestId = node.id;
				}
			} else if (node.type === "group") {
				walk(node.children);
			}
		}
	};
	walk(items);
	return bestId;
};

export type { SideNavItem, NavigationConfig, GroupItem, LeafItem };
